import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import { KanbanCardComponentExtraProps } from '@repo/ui/kanban'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { getLatestPcrPayScale } from '../../../api/pcr-pay-scale/get-latest-pcr-pay-scale'
import { updateScoreCard } from '../../../api/score-cards/update-score-card'
import { getStaffApplicationCurrentScoreCard } from '../../../api/staff-application/get-staff-application-current-score-card'
import useAuth from '../../../lib/hooks/use-auth'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { languageEnumMapping } from '../../../lib/types/enums/languages/language.enum'
import { LanguageLevelEnum } from '../../../lib/types/enums/languages/language-level.enum'
import { priorityEnumMapping } from '../../../lib/types/enums/staff-application/priority.enum'
import { StaffApplicationKanbanStatus } from '../../../lib/types/enums/staff-application/staff-application-kanban-status.enum'
import { StaffApplicationStatusEnum } from '../../../lib/types/enums/staff-application/staff-application-status.enum'
import { staffApplicationTypeMapping } from '../../../lib/types/enums/staff-application/staff-application-type.enum'
import { StaffApplicationLight } from '../../../lib/types/models/staff-application/staff-application'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import CountryFlag from '../../elements/country-flag'
import LoadingScreen from '../../elements/loading/loading-screen'
import {
  mapScoreCardToInput,
  staffApplicationStatusesIndexMapping
} from '../staff-application/utils'
import { formatPcrSalary, getPcrSalaryByStatus } from './pcr-pay-scale.utils'
import { PcrScoreCardModal } from './pcr-score-card-form/pcr-score-card-form'

type Props = {
  data: StaffApplicationLight
  columnId: StaffApplicationKanbanStatus
}

const KanbanTaskCard = ({ data, columnId, isDragging }: Props & KanbanCardComponentExtraProps) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { modalState, toggleModal, closeModel } = useModalManager(['scoreCard'])
  const languageMapping = languageEnumMapping()

  const { data: scoreCard, refetch } = useQuery({
    queryKey: ['score-card', data.id],
    queryFn: () => getStaffApplicationCurrentScoreCard(data.id)
  })

  const { data: payScale } = useQuery({
    queryKey: ['latestPcrPayScale'],
    queryFn: getLatestPcrPayScale
  })

  const { mutate, isPending } = useMutation({
    mutationFn: updateScoreCard,
    onError: showAxiosError,
    onSuccess: () => {
      refetch()

      // invalidate the staff-application-kanban query so that we get the updated data
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('staff-application-kanban')
      })

      closeModel('scoreCard')
    }
  })

  const isLevelOfLanguagesEmptyAndNotPracticed = useMemo(
    () =>
      scoreCard?.levelOfLanguages.every(
        (language) => language.level === LanguageLevelEnum.NOT_PRACTICED
      ),
    [scoreCard?.levelOfLanguages]
  )

  // languages declared by the candidate, shown until the interview sets the levels
  const spokenLanguages = useMemo(
    () =>
      data.languagesTaught.filter(
        (language) => language.level !== LanguageLevelEnum.NOT_PRACTICED
      ),
    [data.languagesTaught]
  )

  if (!scoreCard || !user) return <LoadingScreen />

  const { firstName, lastName, priority } = data
  const salary = scoreCard.paidToPCR
    ? scoreCard.amountPaidToPCR
    : getPcrSalaryByStatus(scoreCard.application.status, payScale)

  const statusDate =
    columnId === StaffApplicationStatusEnum.CLASSIFIED
      ? scoreCard.classifiedDate
      : [
            StaffApplicationStatusEnum.INTERVIEW_SCHEDULED,
            StaffApplicationStatusEnum.INTERVIEW_DONE
          ].includes(columnId as StaffApplicationStatusEnum)
        ? scoreCard.interviewStartDate
        : undefined

  return (
    <div
      className={cn('w-full bg-white rounded-lg p-6 flex flex-col gap-3', {
        'cursor-grabbing border-2 border-primary drop-shadow': isDragging
      })}
      onClick={(e) => {
        e.stopPropagation()
        if (!modalState.scoreCard) toggleModal('scoreCard')
      }}
    >
      <div className={'flex items-start justify-between gap-2'}>
        <Badge
          text={priorityEnumMapping[priority].label}
          variant={priorityEnumMapping[priority].variant}
        />
        {statusDate && (
          <Typography variant={'label-medium'} color={tailwindColors.grey.lighter}>
            {DateFormatter.DDMMYYYY(statusDate)}
          </Typography>
        )}
      </div>
      <Typography variant={'caption'} color={tailwindColors.grey.DEFAULT}>
        {toFullName(firstName, lastName)}
      </Typography>
      <div className={'flex flex-col gap-0'}>
        <div className={'flex flex-wrap gap-1'}>
          <Typography variant={'label-medium'} color={tailwindColors.grey.lighter}>
            Poste :
          </Typography>
          <Typography variant={'label'} color={tailwindColors.grey.lighter}>
            {staffApplicationTypeMapping[scoreCard.application.type].label}
          </Typography>
        </div>

        <div className={'flex flex-wrap gap-1'}>
          <Typography variant={'label-medium'} color={tailwindColors.grey.lighter}>
            Âge :
          </Typography>
          <Typography variant={'label'} color={tailwindColors.grey.lighter}>
            {DateFormatter.calculateAge(scoreCard.application.birthDate)}
          </Typography>
        </div>

        <div className="text-grey-lighter flex gap-1 items-center flex-wrap">
          <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
            Langues :
          </Typography>
          {/* render the language level if it is not NOT_PRACTICED and their is at least one language */}
          {isLevelOfLanguagesEmptyAndNotPracticed ? (
            spokenLanguages.length ? (
              <div className="flex items-center gap-1">
                {spokenLanguages.map((language) => (
                  <CountryFlag
                    key={language.id}
                    code={languageMapping[language.language].countryCode}
                  />
                ))}
              </div>
            ) : (
              <Typography variant="label-semibold" color={tailwindColors.grey.lighter}>
                Pas de niveau de langue
              </Typography>
            )
          ) : (
            <div className="flex items-center gap-1">
              {scoreCard.levelOfLanguages
                .sort((a, b) => {
                  const labelA = languageMapping[a.language].label
                  const labelB = languageMapping[b.language].label
                  return labelA < labelB ? -1 : labelA > labelB ? 1 : 0
                })
                .filter((language) => language.level !== LanguageLevelEnum.NOT_PRACTICED)
                .map((language) => (
                  <CountryFlag
                    key={language.language}
                    code={languageMapping[language.language].countryCode}
                  />
                ))}
            </div>
          )}
        </div>
      </div>
      <div className={'flex flex-wrap gap-1'}>
        <Typography variant={'small-semibold'} color={tailwindColors.grey.lighter}>
          Candidature reçue par le PCR le :
        </Typography>
        <Typography variant={'small-medium'} color={tailwindColors.grey.lighter}>
          {scoreCard.application.dateAssigned
            ? DateFormatter.DDMMYYYY(scoreCard.application.dateAssigned)
            : '-'}
        </Typography>
      </div>
      {salary !== null && (
        <div className="self-end bg-red-light rounded-sm px-3 py-2 min-w-12 text-center">
          <Typography variant="label-semibold" className="text-red">
            {formatPcrSalary(salary)}
          </Typography>
        </div>
      )}
      <PcrScoreCardModal
        defaultTab={staffApplicationStatusesIndexMapping[scoreCard.application.status]}
        status={scoreCard.paidToPCR ? 'PAID' : scoreCard.application.status}
        isOpen={modalState.scoreCard}
        onCancel={() => closeModel('scoreCard')}
        staffApplication={scoreCard.application}
        defaultValues={mapScoreCardToInput(scoreCard)}
        handleSubmit={(values) => mutate({ id: scoreCard.id, ...values })}
        isLoading={isPending}
        handleReset={() => closeModel('scoreCard')}
      />
    </div>
  )
}

export default KanbanTaskCard
